import React, { useState, useEffect, useRef } from 'react';
import { 
  FileText, Printer, CheckCircle2, ShieldCheck, 
  Layers, ArrowLeft, Building2, Calendar, 
  Award, Zap, ChevronRight, ChevronLeft, Gauge,
  Maximize2, Minimize2, Edit3, Sliders, Clock, 
  Thermometer, AlertTriangle, Target, TrendingUp, 
  Wrench, Check, X, Tv, Sparkles, Box, Truck, BarChart3
} from 'lucide-react';
import CompanyLogo from '../CompanyLogo';
import { useCompany } from '../../context/CompanyContext';

// Helper component to render a mini 2D architectural blueprint preview of the warehouse
function WarehouseMiniBlueprint({ mapData }) {
  const areas = mapData?.areas || [];
  const corridors = mapData?.corridors || [];

  if (areas.length === 0 && corridors.length === 0) {
    return (
      <div className="w-full h-full min-h-[220px] bg-slate-950/80 rounded-xl border border-slate-700/80 flex flex-col items-center justify-center text-slate-400 p-4 text-center">
        <Layers size={36} className="text-red-500/60 mb-2 animate-pulse" />
        <span className="text-xs font-semibold text-slate-300">ผังพื้นที่จัดเก็บคลังสินค้า (Layout Blueprint)</span>
        <span className="text-[11px] text-slate-500 mt-1">Selective High-Bay Racking & Traffic Corridors</span>
      </div>
    );
  }

  // Calculate bounding box for SVG
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  [...areas, ...corridors].forEach(item => {
    const x = item.x || 0;
    const y = item.y || 0;
    const w = item.w || 100;
    const h = item.h || 100;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x + w > maxX) maxX = x + w;
    if (y + h > maxY) maxY = y + h;
  });

  const padding = 40;
  const width = Math.max(maxX - minX + padding * 2, 400);
  const height = Math.max(maxY - minY + padding * 2, 300);
  const viewBox = `${minX - padding} ${minY - padding} ${width} ${height}`;

  return (
    <div className="w-full h-full min-h-[220px] bg-slate-950/90 rounded-xl border border-slate-800 p-2 relative overflow-hidden flex items-center justify-center shadow-inner">
      {/* CAD Grid Background */}
      <div 
        className="absolute inset-0 opacity-15 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(#38bdf8 1px, transparent 1px), linear-gradient(90deg, #38bdf8 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}
      />
      <svg viewBox={viewBox} className="w-full h-full max-h-[280px]">
        {/* Render Corridors */}
        {corridors.map((c, idx) => (
          <rect
            key={`cor-${idx}`}
            x={c.x}
            y={c.y}
            width={c.w}
            height={c.h}
            fill="rgba(51, 65, 85, 0.4)"
            stroke="#64748b"
            strokeWidth="1.5"
            strokeDasharray="4 4"
            rx="4"
          />
        ))}

        {/* Render Storage and Flow Areas */}
        {areas.map((a, idx) => {
          const isStorage = a.type?.startsWith('storage');
          const isPickup = a.type === 'pickup';
          const isDropoff = a.type === 'dropoff';

          let fillColor = 'rgba(234, 88, 12, 0.25)';
          let strokeColor = '#ea580c';
          if (isStorage) {
            fillColor = 'rgba(37, 99, 235, 0.35)';
            strokeColor = '#3b82f6';
          } else if (isPickup) {
            fillColor = 'rgba(16, 185, 129, 0.35)';
            strokeColor = '#10b981';
          } else if (isDropoff) {
            fillColor = 'rgba(244, 63, 94, 0.35)';
            strokeColor = '#f43f5e';
          }

          return (
            <g key={`area-${idx}`}>
              <rect
                x={a.x}
                y={a.y}
                width={a.w}
                height={a.h}
                fill={fillColor}
                stroke={strokeColor}
                strokeWidth="2"
                rx="4"
              />
              {/* Racking cross lines for storage racks */}
              {isStorage && (
                <>
                  <line x1={a.x} y1={a.y + a.h / 2} x2={a.x + a.w} y2={a.y + a.h / 2} stroke={strokeColor} strokeWidth="1" strokeDasharray="3 3" opacity="0.7" />
                  <line x1={a.x + a.w * 0.33} y1={a.y} x2={a.x + a.w * 0.33} y2={a.y + a.h} stroke={strokeColor} strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
                  <line x1={a.x + a.w * 0.66} y1={a.y} x2={a.x + a.w * 0.66} y2={a.y + a.h} stroke={strokeColor} strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
                </>
              )}
              <text
                x={a.x + a.w / 2}
                y={a.y + a.h / 2 + 3}
                fill="#f8fafc"
                fontSize="10"
                fontWeight="bold"
                textAnchor="middle"
                style={{ pointerEvents: 'none' }}
              >
                {a.label || (isStorage ? `Rack ${idx + 1}` : isPickup ? 'Inbound' : isDropoff ? 'Outbound' : 'Area')}
              </text>
            </g>
          );
        })}
      </svg>
      {/* Legend Badge */}
      <div className="absolute bottom-2 left-2 flex items-center gap-3 bg-slate-900/90 border border-slate-700/80 px-2.5 py-1 rounded-md text-[10px] text-slate-300">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-xs bg-blue-500" /> แร็คจัดเก็บ</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-xs bg-emerald-500" /> จุดรับ Inbound</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-xs bg-rose-500" /> จุดส่ง Outbound</span>
      </div>
    </div>
  );
}

export default function ProposalDocumentView({ 
  proposal, 
  mapData, 
  config, 
  selectedForklift, 
  onBack,
  onUpdateProposal 
}) {
  const { companyName } = useCompany();
  const printRef = useRef(null);
  const slideDeckRef = useRef(null);

  // View mode: 'slides' (PowerPoint deck) or 'document' (A4 formal printable document)
  const [viewMode, setViewMode] = useState('slides');
  const [activeSlide, setActiveSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isEditingOnsite, setIsEditingOnsite] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Storage & capacity calculations
  const rackAreas = (mapData?.areas || []).filter(a => a.type?.startsWith('storage'));
  const totalPalletsCalculated = rackAreas.reduce((sum, a) => {
    if (a.palletCount) return sum + a.palletCount;
    const levels = a.levels || 4;
    const bays = a.bays || Math.max(2, Math.round((a.w || 200) / 30));
    return sum + (levels * bays * 2);
  }, 0);

  const totalPallets = proposal?.proposal_summary?.total_pallet_capacity || totalPalletsCalculated || 2400;
  const warehouseArea = proposal?.proposal_summary?.warehouse_area_sqm || 2200;
  const clearHeight = proposal?.proposal_summary?.clear_height_m || 11.5;
  const recommendedFleet = proposal?.proposal_summary?.recommended_fleet || config?.fleetSize || 3;
  const throughput = proposal?.proposal_summary?.throughput_pallets_hr || Math.round((recommendedFleet * 14) + 5);

  const forklift = selectedForklift || proposal?.forklift_config || {
    model: 'BT Reflex RRE160H',
    series: 'BT Reflex R-series',
    category: 'Reach truck',
    load_capacity_kg: 1600,
    lift_height_mm: 7000,
    overall_width_mm: 1270,
    overall_length_mm: 2420,
    turning_radius_mm: 1720,
    aisle_width_1000x1200_mm: 2750,
    battery_voltage_v: 48,
    battery_capacity_ah: 620,
    image_url: '/toyota_forklifts/BT_Reflex_R_Series.png'
  };

  // Shift & Break Calculations from Step 1 Section 6
  const shiftConfig = mapData?.shiftConfig || proposal?.map_data?.shiftConfig || {};
  const shiftsPerDay = shiftConfig.shiftsPerDay || 2;
  const shiftHours = shiftConfig.shiftHours || 8;
  const lunchBreakMinutes = shiftConfig.lunchBreakMinutes !== undefined ? shiftConfig.lunchBreakMinutes : 60;
  const shortBreakMinutes = shiftConfig.shortBreakMinutes !== undefined ? shiftConfig.shortBreakMinutes : 15;
  const shortBreakCount = shiftConfig.shortBreakCount !== undefined ? shiftConfig.shortBreakCount : 2;
  const totalBreakMinutes = lunchBreakMinutes + (shortBreakMinutes * shortBreakCount);
  const netHoursPerShift = Number(Math.max(1, (shiftHours * 60 - totalBreakMinutes) / 60).toFixed(1));
  const netDailyHours = Number((netHoursPerShift * shiftsPerDay).toFixed(1));
  const defaultShiftsText = `${shiftsPerDay} กะ (${shiftsPerDay * shiftHours} ชม./วัน - สุทธิ ${netDailyHours} ชม.)`;

  // Customer Onsite Information State
  const [onsiteForm, setOnsiteForm] = useState({
    customer_name: proposal?.customer_name || 'บริษัทคู่ค้าชั้นนำ',
    title: proposal?.title || 'ข้อเสนอโครงการออกแบบระบบคลังสินค้าและยานยนต์ลำเลียง',
    industry: proposal?.industry || 'อาหารและเครื่องดื่ม (F&B / Cold Chain)',
    warehouse_area_sqm: warehouseArea,
    clear_height_m: clearHeight,
    temperature: proposal?.proposal_summary?.temperature || 'อุณหภูมิห้องปกติ (Ambient 25°C)',
    working_shifts: proposal?.proposal_summary?.working_shifts || defaultShiftsText,
    pallet_weight_kg: proposal?.proposal_summary?.pallet_weight_kg || 1000,
    current_capacity: proposal?.proposal_summary?.current_capacity || '1,600 พาเลท (ความจุเต็ม 95%)',
    pain_points: proposal?.proposal_summary?.pain_points || [
      'พื้นที่จัดเก็บไม่เพียงพอต่อปริมาณสต็อกสินค้าที่เติบโตต่อเนื่อง',
      'ช่องทางวิ่งเดิมกว้างเกินไป ทำให้สูญเสียพื้นที่จัดเก็บเชิงลึก (Lost Floor Space)',
      'การหยิบสินค้าช่วงพีคมีปัญหาคอขวด รอบเวลาลำเลียงไม่ทันตามกำหนด',
      'ความกังวลด้านความปลอดภัยเมื่อยกสินค้าที่ระดับความสูงเกิน 8 เมตร'
    ],
    goals: proposal?.proposal_summary?.goals || [
      'เพิ่มความจุพาเลทขึ้นอย่างน้อย +30% ถึง +40% ในพื้นที่อาคารเดิม',
      'ปรับลดขนาดช่องทางวิ่ง (Narrow Aisle) เพื่อขยายพื้นที่แร็คอย่างปลอดภัย',
      'เพิ่ม Throughput การรับ-จ่ายสินค้าให้ได้มากกว่า 45 พาเลท/ชั่วโมง',
      'ลดต้นทุนการดำเนินงานและคืนทุนภายในระยะเวลา 12-18 เดือน'
    ],
    payback_months: proposal?.proposal_summary?.payback_months || '12-18'
  });

  // Sync state if proposal changes
  useEffect(() => {
    if (proposal) {
      setOnsiteForm(prev => ({
        ...prev,
        customer_name: proposal.customer_name || prev.customer_name,
        title: proposal.title || prev.title,
        industry: proposal.industry || prev.industry,
        warehouse_area_sqm: proposal.proposal_summary?.warehouse_area_sqm || prev.warehouse_area_sqm,
        clear_height_m: proposal.proposal_summary?.clear_height_m || prev.clear_height_m,
        temperature: proposal.proposal_summary?.temperature || prev.temperature,
        working_shifts: proposal.proposal_summary?.working_shifts || defaultShiftsText,
        pain_points: proposal.proposal_summary?.pain_points || prev.pain_points,
        goals: proposal.proposal_summary?.goals || prev.goals,
        payback_months: proposal.proposal_summary?.payback_months || prev.payback_months,
      }));
    }
  }, [proposal, defaultShiftsText]);

  // Handle Fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      slideDeckRef.current?.requestFullscreen?.().catch(err => console.error(err));
    } else {
      document.exitFullscreen?.().catch(err => console.error(err));
    }
  };

  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  // Keyboard navigation for PowerPoint slides (ArrowLeft, ArrowRight, Space)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isEditingOnsite) return; // don't capture if typing in modal
      if (viewMode !== 'slides') return;

      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        setActiveSlide(prev => Math.min(prev + 1, 5));
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        setActiveSlide(prev => Math.max(prev - 1, 0));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode, isEditingOnsite]);

  const handlePrint = () => {
    window.print();
  };

  // Save modified onsite details to backend
  const handleSaveOnsiteDetails = async () => {
    if (!proposal?.id) {
      setIsEditingOnsite(false);
      return;
    }
    setIsSaving(true);
    try {
      const updatedSummary = {
        ...(proposal.proposal_summary || {}),
        warehouse_area_sqm: Number(onsiteForm.warehouse_area_sqm),
        clear_height_m: Number(onsiteForm.clear_height_m),
        temperature: onsiteForm.temperature,
        working_shifts: onsiteForm.working_shifts,
        pallet_weight_kg: Number(onsiteForm.pallet_weight_kg),
        current_capacity: onsiteForm.current_capacity,
        pain_points: onsiteForm.pain_points,
        goals: onsiteForm.goals,
        payback_months: onsiteForm.payback_months,
        total_pallet_capacity: totalPallets,
        recommended_fleet: recommendedFleet,
        throughput_pallets_hr: throughput
      };

      const res = await fetch(`/api/proposals/${proposal.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: onsiteForm.title,
          customer_name: onsiteForm.customer_name,
          industry: onsiteForm.industry,
          proposal_summary: updatedSummary
        })
      });

      const data = await res.json();
      if (data.status === 'success') {
        if (onUpdateProposal) {
          onUpdateProposal({
            title: onsiteForm.title,
            customer_name: onsiteForm.customer_name,
            industry: onsiteForm.industry,
            proposal_summary: updatedSummary
          });
        }
        setIsEditingOnsite(false);
      } else {
        alert('เกิดข้อผิดพลาดในการบันทึก: ' + (data.message || ''));
      }
    } catch (err) {
      console.error(err);
      alert('ไม่สามารถบันทึกข้อมูลหน้างานได้');
    } finally {
      setIsSaving(false);
    }
  };

  /* ─────────────────────────────────────────────────────────────────────────────
     SLIDE DEFINITIONS (Total 6 Slides)
  ───────────────────────────────────────────────────────────────────────────── */
  const SLIDES = [
    {
      id: 'cover',
      title: 'หน้าปกโครงการ',
      subtitle: 'Cover & Title Slide',
      render: () => (
        <div className="h-full w-full flex flex-col justify-between p-8 sm:p-12 relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-red-950 text-white select-none">
          {/* Subtle Background Watermark & Grid */}
          <div className="absolute -right-12 -bottom-16 opacity-5 font-black text-[180px] tracking-tighter pointer-events-none text-white select-none">
            TOYOTA
          </div>
          <div 
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.2) 1px, transparent 0)',
              backgroundSize: '24px 24px'
            }}
          />

          {/* Top Brand Header */}
          <div className="relative z-10 flex items-center justify-between border-b border-slate-800 pb-4">
            <div className="flex items-center gap-3">
              <div className="bg-white px-3 py-1 rounded-md shadow-xs">
                <CompanyLogo variant="proposal" showSubtitle={false} />
              </div>
              <div>
                <div className="text-xs font-bold tracking-widest text-red-500 uppercase">
                  Toyota Solution Proposal
                </div>
                <div className="text-[11px] text-slate-400">Warehouse Engineering & Automation Division</div>
              </div>
            </div>

            <div className="text-right">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-600/30 border border-red-500/40 text-red-300 rounded-full text-xs font-bold">
                <Sparkles size={12} /> Solution Deck 2026
              </span>
            </div>
          </div>

          {/* Center Main Hero */}
          <div className="relative z-10 my-auto py-6 max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-slate-800/80 border border-slate-700 px-3 py-1 rounded-lg text-xs font-semibold text-slate-300 mb-4">
              <Building2 size={14} className="text-red-400" />
              ข้อเสนอโครงการสำหรับ: <strong className="text-white font-bold text-sm">{onsiteForm.customer_name}</strong>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-tight">
              {onsiteForm.title}
            </h1>

            <p className="text-slate-300 text-sm sm:text-base mt-4 leading-relaxed font-light">
              การออกแบบบูรณาการระบบจัดเก็บคลังสินค้า (Racking System), การเลือกรถยกโตโยต้าที่สอดคล้องกับพิกัดน้ำหนักและช่องทางวิ่ง, พร้อมผลการจำลองทราฟฟิก (Traffic Simulation) เพื่อประสิทธิภาพและผลตอบแทนการลงทุนสูงสุด
            </p>

            {/* Feature Highlight Pills */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-6">
              <span className="bg-slate-800/90 text-slate-200 border border-slate-700 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5">
                <Box size={14} className="text-blue-400" /> ความจุ {totalPallets.toLocaleString()} พาเลท
              </span>
              <span className="bg-slate-800/90 text-slate-200 border border-slate-700 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5">
                <Truck size={14} className="text-orange-400" /> {forklift.model}
              </span>
              <span className="bg-slate-800/90 text-slate-200 border border-slate-700 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5">
                <Gauge size={14} className="text-emerald-400" /> Throughput {throughput} P/ชม.
              </span>
              <span className="bg-slate-800/90 text-slate-200 border border-slate-700 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5">
                <TrendingUp size={14} className="text-amber-400" /> คืนทุน {onsiteForm.payback_months} เดือน
              </span>
            </div>
          </div>

          {/* Bottom Footer Info */}
          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between pt-4 border-t border-slate-800/90 text-xs text-slate-400 gap-2">
            <div>
              <span className="font-semibold text-slate-200">{companyName || 'Toyota Material Handling (Thailand) Co., Ltd.'}</span>
              <span className="mx-2 text-slate-600">|</span>
              <span>กลุ่มอุตสาหกรรม: {onsiteForm.industry}</span>
            </div>
            <div className="flex items-center gap-3">
              <span>วันที่: {new Date().toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              <button 
                onClick={() => setActiveSlide(1)}
                className="inline-flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white font-bold px-3 py-1.5 rounded-lg text-xs transition-colors shadow-xs cursor-pointer"
              >
                เริ่มการนำเสนอ <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'customer_profile',
      title: 'ข้อมูลลูกค้า & สภาพหน้างาน',
      subtitle: 'Customer Profile & Site Challenges',
      render: () => (
        <div className="h-full w-full flex flex-col justify-between p-6 sm:p-10 bg-slate-900 text-white select-none">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <div>
              <span className="text-xs font-bold text-red-500 uppercase tracking-wider">ส่วนที่ 1: การวิเคราะห์หน้างาน</span>
              <h2 className="text-xl sm:text-2xl font-extrabold text-white flex items-center gap-2">
                <Building2 size={22} className="text-red-500" />
                ข้อมูลของลูกค้าหน้างานและสภาพแวดล้อมคลังสินค้า
              </h2>
            </div>
            <button
              onClick={() => setIsEditingOnsite(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 hover:text-white rounded-lg text-xs font-semibold transition-colors cursor-pointer"
              title="แก้ไขข้อมูลลูกค้าและปัญหาหน้างาน"
            >
              <Edit3 size={13} className="text-amber-400" /> แก้ไขข้อมูลหน้างาน
            </button>
          </div>

          {/* Body 2 Columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 my-auto py-3">
            {/* Left: Physical Parameters */}
            <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-4 sm:p-5 flex flex-col justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-200 mb-3 flex items-center gap-2">
                  <Sliders size={16} className="text-blue-400" />
                  คุณลักษณะทางกายภาพหน้างาน (Onsite Parameters)
                </h3>
                <div className="space-y-2.5 text-xs">
                  <div className="flex justify-between py-1.5 border-b border-slate-800/80">
                    <span className="text-slate-400 flex items-center gap-1.5"><Building2 size={13} /> ชื่อลูกค้า / สถานประกอบการ</span>
                    <span className="font-bold text-white text-right">{onsiteForm.customer_name}</span>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-slate-800/80">
                    <span className="text-slate-400 flex items-center gap-1.5"><Box size={13} /> ขนาดพื้นที่คลังสินค้า</span>
                    <span className="font-bold text-amber-400 text-right">{Number(onsiteForm.warehouse_area_sqm).toLocaleString()} ตร.ม. (sqm)</span>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-slate-800/80">
                    <span className="text-slate-400 flex items-center gap-1.5"><Layers size={13} /> ความสูงใต้โครงสร้าง (Clear Height)</span>
                    <span className="font-bold text-amber-400 text-right">{onsiteForm.clear_height_m} เมตร (m)</span>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-slate-800/80">
                    <span className="text-slate-400 flex items-center gap-1.5"><Thermometer size={13} /> สภาพแวดล้อมอุณหภูมิ</span>
                    <span className="font-semibold text-slate-200 text-right">{onsiteForm.temperature}</span>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-slate-800/80">
                    <span className="text-slate-400 flex items-center gap-1.5"><Clock size={13} /> ชั่วโมงการทำงาน</span>
                    <span className="font-semibold text-slate-200 text-right">{onsiteForm.working_shifts}</span>
                  </div>
                  <div className="flex justify-between py-1.5">
                    <span className="text-slate-400 flex items-center gap-1.5"><Box size={13} /> ขนาดและน้ำหนักพาเลท</span>
                    <span className="font-semibold text-slate-200 text-right">
                      {mapData?.palletSize?.width || 1000} x {mapData?.palletSize?.length || 1200} มม. (~{onsiteForm.pallet_weight_kg} kg)
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-800 text-[11px] text-slate-400 flex items-center justify-between">
                <span>ความจุคลังสินค้าเดิม:</span>
                <span className="font-bold text-red-400">{onsiteForm.current_capacity}</span>
              </div>
            </div>

            {/* Right: Pain Points & Targets */}
            <div className="space-y-4">
              {/* Pain Points Card */}
              <div className="bg-red-950/30 border border-red-900/50 rounded-xl p-4">
                <h3 className="text-xs font-bold text-red-400 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                  <AlertTriangle size={15} className="text-red-400" />
                  ปัญหาและความท้าทายหน้างานปัจจุบัน (Customer Pain Points)
                </h3>
                <ul className="space-y-2 text-xs text-slate-300">
                  {onsiteForm.pain_points.map((pt, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-red-500 font-bold shrink-0 mt-0.5">•</span>
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Target Goals Card */}
              <div className="bg-emerald-950/30 border border-emerald-900/50 rounded-xl p-4">
                <h3 className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                  <Target size={15} className="text-emerald-400" />
                  เป้าหมายที่ต้องการบรรลุในโครงการนี้ (Project Goals)
                </h3>
                <ul className="space-y-2 text-xs text-slate-300">
                  {onsiteForm.goals.map((g, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-emerald-400 font-bold shrink-0 mt-0.5">✓</span>
                      <span>{g}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-slate-500 border-t border-slate-800 pt-3">
            <span>Toyota Material Handling Thailand — Engineering Assessment</span>
            <span>สไลด์ 02 / 06</span>
          </div>
        </div>
      )
    },
    {
      id: 'layout_racking',
      title: 'ผังคลังสินค้า & ระบบแร็คจัดเก็บ',
      subtitle: 'Proposed Layout & Racking Solution',
      render: () => (
        <div className="h-full w-full flex flex-col justify-between p-6 sm:p-10 bg-slate-900 text-white select-none">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <div>
              <span className="text-xs font-bold text-blue-400 uppercase tracking-wider">ส่วนที่ 2: โซลูชันผังและระบบจัดเก็บ</span>
              <h2 className="text-xl sm:text-2xl font-extrabold text-white flex items-center gap-2">
                <Layers size={22} className="text-blue-500" />
                ผังคลังสินค้าและระบบแร็คจัดเก็บที่นำเสนอ (Proposed Racking Solution)
              </h2>
            </div>
            <div className="text-right">
              <span className="text-xs text-slate-400 font-medium">ความจุจัดเก็บรวมที่เพิ่มขึ้น:</span>
              <div className="text-lg font-black text-emerald-400">+{Math.round((totalPallets / (parseInt(onsiteForm.current_capacity) || 1600) - 1) * 100)}% พาเลท</div>
            </div>
          </div>

          {/* Body: Blueprint + Spec Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 my-auto py-3 items-center">
            {/* Left 7 Cols: Mini 2D Blueprint */}
            <div className="lg:col-span-7 h-[260px] sm:h-[300px]">
              <WarehouseMiniBlueprint mapData={mapData} />
            </div>

            {/* Right 5 Cols: Storage Breakdown */}
            <div className="lg:col-span-5 space-y-3">
              {/* Capacity Card */}
              <div className="bg-gradient-to-r from-blue-950/60 to-slate-950/80 border border-blue-800/60 rounded-xl p-4">
                <div className="text-xs text-blue-300 font-bold uppercase">ความจุจัดเก็บรวมทั้งคลัง (Total Pallet Capacity)</div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-3xl font-black text-white">{totalPallets.toLocaleString()}</span>
                  <span className="text-xs text-slate-400">พาเลท (Pallets)</span>
                </div>
                <div className="text-[11px] text-blue-300/80 mt-1">
                  จัดสรรผ่านระบบ Selective High-Bay Racking สอดคล้องกับพิกัดความสูงอาคาร {onsiteForm.clear_height_m} เมตร
                </div>
              </div>

              {/* Racking Specs List */}
              <div className="bg-slate-950/70 border border-slate-800 rounded-xl p-3.5 space-y-2 text-xs">
                <div className="flex justify-between py-1 border-b border-slate-800">
                  <span className="text-slate-400">ประเภทระบบแร็ค:</span>
                  <span className="font-semibold text-white">Selective Racking System (Heavy Duty)</span>
                </div>
                <div className="flex justify-between py-1 border-b border-slate-800">
                  <span className="text-slate-400">จำนวนระดับชั้นจัดเก็บ:</span>
                  <span className="font-semibold text-white">5 - 6 ระดับชั้น (Beam Levels)</span>
                </div>
                <div className="flex justify-between py-1 border-b border-slate-800">
                  <span className="text-slate-400">ขนาดช่องทางวิ่ง Aisle ที่ออกแบบ:</span>
                  <span className="font-bold text-amber-400">
                    {forklift?.aisle_width_1000x1200_mm ? `${(forklift.aisle_width_1000x1200_mm / 1000).toFixed(2)} ม.` : '2.75 เมตร'}
                  </span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-slate-400">สถานีรับ-ส่งสินค้า (In/Out Docks):</span>
                  <span className="font-semibold text-white">
                    {(mapData?.areas || []).filter(a => a.type === 'pickup' || a.type === 'dropoff').length || 2} จุดตรวจรับ
                  </span>
                </div>
              </div>

              <div className="bg-emerald-950/30 border border-emerald-800/40 rounded-lg p-2.5 flex items-center gap-2 text-xs text-emerald-300">
                <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
                <span>พื้นที่ทางเดินแคบลง 18% ทำให้สามารถเพิ่มแถวแร็คได้อีก 2 แถวใหญ่</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-slate-500 border-t border-slate-800 pt-3">
            <span>Toyota Material Handling Thailand — Optimized Warehouse Layout</span>
            <span>สไลด์ 03 / 06</span>
          </div>
        </div>
      )
    },
    {
      id: 'toyota_forklift',
      title: 'รุ่นรถยก Toyota & สเปกที่แนะนำ',
      subtitle: 'Recommended Equipment & Specs',
      render: () => (
        <div className="h-full w-full flex flex-col justify-between p-6 sm:p-10 bg-slate-900 text-white select-none">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <div>
              <span className="text-xs font-bold text-red-500 uppercase tracking-wider">ส่วนที่ 3: รถยกโตโยต้าที่แนะนำ</span>
              <h2 className="text-xl sm:text-2xl font-extrabold text-white flex items-center gap-2">
                <Award size={22} className="text-red-500" />
                รุ่นรถยกโตโยต้าและสเปกสมรรถนะเชิงวิศวกรรม (Equipment Specifications)
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <span className="bg-red-600 text-white text-xs font-bold px-2.5 py-1 rounded">
                Toyota Standard
              </span>
            </div>
          </div>

          {/* Body: Photo & Spec Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 my-auto py-3 items-center">
            {/* Left 4 Cols: Vehicle Photo Card */}
            <div className="md:col-span-4 bg-slate-950/90 border border-slate-800 rounded-2xl p-5 flex flex-col items-center justify-center text-center shadow-inner relative overflow-hidden">
              <div className="w-full h-44 flex items-center justify-center bg-white/5 rounded-xl p-2 mb-3">
                {forklift?.image_url ? (
                  <img 
                    src={forklift.image_url} 
                    alt={forklift.model} 
                    className="max-h-full max-w-full object-contain filter drop-shadow-lg"
                  />
                ) : (
                  <Truck size={64} className="text-red-500/60 animate-pulse" />
                )}
              </div>
              <div className="text-xs text-red-400 font-bold uppercase">{forklift?.series || 'Toyota Premium Series'}</div>
              <h3 className="text-xl font-black text-white mt-0.5">{forklift?.model || 'BT Reflex RRE160H'}</h3>
              <div className="text-[11px] text-slate-400 mt-1">{forklift?.category || 'High-Performance Reach Truck'}</div>
            </div>

            {/* Right 8 Cols: 6 Spec Metric Tiles */}
            <div className="md:col-span-8">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3">
                  <div className="text-[11px] text-slate-400">พิกัดยกสูงสุด (Capacity)</div>
                  <div className="text-lg font-black text-white mt-1">{forklift?.load_capacity_kg || 1600} kg</div>
                  <div className="text-[10px] text-slate-500">รองรับพาเลท 1,000 kg ได้ 100%</div>
                </div>

                <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3">
                  <div className="text-[11px] text-slate-400">ระยะยกสูง (Lift Height)</div>
                  <div className="text-lg font-black text-amber-400 mt-1">{((forklift?.lift_height_mm || 7000) / 1000).toFixed(2)} m</div>
                  <div className="text-[10px] text-slate-500">ครอบคลุมระดับแร็คชั้นบนสุด</div>
                </div>

                <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3">
                  <div className="text-[11px] text-slate-400">ช่องทางวิ่งขั้นต่ำ (Aisle Ast)</div>
                  <div className="text-lg font-black text-blue-400 mt-1">{forklift?.aisle_width_1000x1200_mm || 2750} mm</div>
                  <div className="text-[10px] text-slate-500">ประหยัดพื้นที่ช่องวิ่งสูงสุด</div>
                </div>

                <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3">
                  <div className="text-[11px] text-slate-400">รัศมีวงเลี้ยว (Turning Radius)</div>
                  <div className="text-lg font-black text-white mt-1">{forklift?.turning_radius_mm || 1720} mm</div>
                  <div className="text-[10px] text-slate-500">เลี้ยวคล่องตัวแม้พื้นที่แคบ</div>
                </div>

                <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3">
                  <div className="text-[11px] text-slate-400">ระบบพลังงานแบตเตอรี่</div>
                  <div className="text-lg font-black text-emerald-400 mt-1">
                    {forklift?.battery_voltage_v ? `${forklift.battery_voltage_v}V` : '48V Li-ion'}
                  </div>
                  <div className="text-[10px] text-slate-500">รองรับ Opportunity Fast Charge</div>
                </div>

                <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3">
                  <div className="text-[11px] text-slate-400">ความเร็วเคลื่อนที่ (Speed)</div>
                  <div className="text-lg font-black text-white mt-1">
                    {forklift?.travel_speed_loaded_kmh ? `${forklift.travel_speed_loaded_kmh} km/h` : '12.0 km/h'}
                  </div>
                  <div className="text-[10px] text-slate-500">เพิ่มรอบ Throughput ต่อกะ</div>
                </div>
              </div>

              {/* Safety ISO Verification */}
              <div className="mt-3 bg-emerald-950/40 border border-emerald-800/60 rounded-xl p-3 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <ShieldCheck size={20} className="text-emerald-400 shrink-0" />
                  <div>
                    <div className="text-xs font-bold text-emerald-300">ผ่านการคำนวณ Residual Capacity เสถียรภาพความปลอดภัย 100%</div>
                    <div className="text-[11px] text-emerald-400/80">ระบบ Toyota SAS ควบคุมมุมกระโดงและลดความเร็วขณะเลี้ยว ป้องกันการพลิกคว่ำตามมาตรฐาน ISO 22915</div>
                  </div>
                </div>
                <span className="text-xs font-black text-emerald-300 bg-emerald-900/60 px-2.5 py-1 rounded-full whitespace-nowrap">
                  100% Safety Pass
                </span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-slate-500 border-t border-slate-800 pt-3">
            <span>Toyota Material Handling Thailand — Fleet Specification</span>
            <span>สไลด์ 04 / 06</span>
          </div>
        </div>
      )
    },
    {
      id: 'simulation_throughput',
      title: 'ผลการจำลองทราฟฟิก & Throughput',
      subtitle: 'Simulation & Throughput Results',
      render: () => (
        <div className="h-full w-full flex flex-col justify-between p-6 sm:p-10 bg-slate-900 text-white select-none">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <div>
              <span className="text-xs font-bold text-violet-400 uppercase tracking-wider">ส่วนที่ 4: การจำลองทางคณิตศาสตร์</span>
              <h2 className="text-xl sm:text-2xl font-extrabold text-white flex items-center gap-2">
                <Gauge size={22} className="text-violet-500" />
                ผลการจำลองทราฟฟิกและปริมาณการลำเลียง (Traffic Simulation Analysis)
              </h2>
            </div>
            <div className="text-right">
              <span className="text-xs text-slate-400">สถานะการจำลอง:</span>
              <div className="text-xs font-bold text-emerald-400 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> ผ่านเกณฑ์ OEE 85%
              </div>
            </div>
          </div>

          {/* Body: 4 Main KPI Cards + Operational Breakdown */}
          <div className="space-y-4 my-auto py-2">
            {/* 4 Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
              <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-4 text-center">
                <div className="text-[11px] font-bold text-violet-400 uppercase">ฟลีทรถยกที่แนะนำ</div>
                <div className="text-3xl font-black text-white mt-1">{recommendedFleet}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">คัน (Fleet Units)</div>
              </div>

              <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-4 text-center">
                <div className="text-[11px] font-bold text-blue-400 uppercase">รอบเวลาเฉลี่ย (Cycle Time)</div>
                <div className="text-3xl font-black text-blue-400 mt-1">115</div>
                <div className="text-[11px] text-slate-400 mt-0.5">วินาที / พาเลท (Sec/Pallet)</div>
              </div>

              <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-4 text-center">
                <div className="text-[11px] font-bold text-emerald-400 uppercase">Throughput ต่อชั่วโมง</div>
                <div className="text-3xl font-black text-emerald-400 mt-1">{throughput}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">พาเลท / ชม. (Pallets/Hr)</div>
              </div>

              <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-4 text-center">
                <div className="text-[11px] font-bold text-amber-400 uppercase">กำลังลำเลียงสุทธิ ({netDailyHours} ชม./วัน)</div>
                <div className="text-3xl font-black text-amber-400 mt-1">{Math.round(throughput * netDailyHours).toLocaleString()}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">พาเลท / วัน ({shiftsPerDay} กะ)</div>
              </div>
            </div>

            {/* Operational Analysis Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
              <div className="bg-slate-950/60 border border-slate-800 rounded-xl p-3.5">
                <div className="text-xs font-bold text-slate-300 mb-1 flex items-center gap-1.5">
                  <BarChart3 size={14} className="text-violet-400" /> อัตราการใช้รถ (Fleet Utilization)
                </div>
                <div className="text-sm font-semibold text-white">82% (สมดุลสูง ไม่เกิดคอขวด)</div>
                <p className="text-[11px] text-slate-400 mt-1">มี Safety Buffer 18% รองรับความผันผวนของยอดออเดอร์ในแต่ละชั่วโมง</p>
              </div>

              <div className="bg-slate-950/60 border border-slate-800 rounded-xl p-3.5">
                <div className="text-xs font-bold text-slate-300 mb-1 flex items-center gap-1.5">
                  <Zap size={14} className="text-amber-400" /> การรองรับ Peak Surge
                </div>
                <div className="text-sm font-semibold text-white">{Math.round(throughput * 1.25)} พาเลท / ชั่วโมง</div>
                <p className="text-[11px] text-slate-400 mt-1">สามารถเร่งการทำงานช่วงเร่งด่วนได้ทันตามเวลาปล่อยรถขนส่ง</p>
              </div>

              <div className="bg-slate-950/60 border border-slate-800 rounded-xl p-3.5">
                <div className="text-xs font-bold text-slate-300 mb-1 flex items-center gap-1.5">
                  <Clock size={14} className="text-emerald-400" /> Opportunity Charging Strategy
                </div>
                <div className="text-sm font-semibold text-white">ชาร์จช่วงพัก {totalBreakMinutes} นาที/กะ</div>
                <p className="text-[11px] text-slate-400 mt-1">
                  {shiftsPerDay >= 2
                    ? `แบตเตอรี่ Li-ion คืนประจุ ~${Math.min(90, Math.round(totalBreakMinutes * 0.85))}% วิ่งต่อเนื่องได้ครบทั้ง ${shiftsPerDay} กะ ไม่ต้องสลับแบตเตอรี่`
                    : 'รอบทำงาน 1 กะ รองรับทั้งชาร์จปกติข้ามคืน หรือ Fast Charge เสริมระหว่างวันได้อย่างยืดหยุ่น'}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-slate-500 border-t border-slate-800 pt-3">
            <span>Toyota Material Handling Thailand — Simulation Certified</span>
            <span>สไลด์ 05 / 06</span>
          </div>
        </div>
      )
    },
    {
      id: 'roi_summary',
      title: 'สรุปความคุ้มค่า & ผลตอบแทน ROI',
      subtitle: 'Value Proposition & ROI Summary',
      render: () => (
        <div className="h-full w-full flex flex-col justify-between p-6 sm:p-10 bg-slate-900 text-white select-none">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <div>
              <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">ส่วนที่ 5: สรุปความคุ้มค่าโครงการ</span>
              <h2 className="text-xl sm:text-2xl font-extrabold text-white flex items-center gap-2">
                <TrendingUp size={22} className="text-emerald-500" />
                สรุปความคุ้มค่า ผลตอบแทนการลงทุน (ROI) และการบริการหลังการขาย
              </h2>
            </div>
            <div className="bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 px-3 py-1 rounded-full text-xs font-bold">
              Payback: {onsiteForm.payback_months} เดือน
            </div>
          </div>

          {/* Body: 3 Pillars */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-auto py-2">
            {/* Pillar 1: Financial & Capacity */}
            <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-4 flex flex-col justify-between">
              <div>
                <div className="w-8 h-8 rounded-lg bg-emerald-950/80 border border-emerald-800 text-emerald-400 flex items-center justify-center font-black text-sm mb-3">
                  💰
                </div>
                <h3 className="text-sm font-bold text-white mb-2">ความคุ้มค่าทางการเงิน & การจัดเก็บ</h3>
                <ul className="space-y-2 text-xs text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-400 font-bold">✓</span>
                    <span>เพิ่มความจุคลังขึ้น <strong>+35%</strong> โดยไม่ต้องลงทุนสร้างอาคารใหม่</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-400 font-bold">✓</span>
                    <span>ลดเวลารอบการทำงาน (Cycle Time) ลง <strong>25%</strong> ลดต้นทุนต่อพาเลท</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-400 font-bold">✓</span>
                    <span>ระยะเวลาคืนทุนโดยประมาณ <strong>{onsiteForm.payback_months} เดือน</strong></span>
                  </li>
                </ul>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-800/80 text-[11px] text-emerald-400 font-semibold">
                ผลตอบแทนการลงทุนสูง คุ้มค่าในระยะยาว
              </div>
            </div>

            {/* Pillar 2: Safety & Standard */}
            <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-4 flex flex-col justify-between">
              <div>
                <div className="w-8 h-8 rounded-lg bg-blue-950/80 border border-blue-800 text-blue-400 flex items-center justify-center font-black text-sm mb-3">
                  🛡️
                </div>
                <h3 className="text-sm font-bold text-white mb-2">ความปลอดภัยและมาตรฐานสากล</h3>
                <ul className="space-y-2 text-xs text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 font-bold">✓</span>
                    <span>ระบบ <strong>Toyota SAS</strong> ลิขสิทธิ์เฉพาะ ป้องกันอุบัติเหตุพลิกคว่ำ</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 font-bold">✓</span>
                    <span>ผ่านการคำนวณ <strong>Residual Capacity</strong> ยกสูง 100% ปลอดภัย</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 font-bold">✓</span>
                    <span>โครงสร้างแร็คตามมาตรฐานวิศวกรรมสากล รองรับแผ่นดินไหว</span>
                  </li>
                </ul>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-800/80 text-[11px] text-blue-400 font-semibold">
                ยกระดับความปลอดภัยสู่ Zero-Accident
              </div>
            </div>

            {/* Pillar 3: Toyota After-Sales */}
            <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-4 flex flex-col justify-between">
              <div>
                <div className="w-8 h-8 rounded-lg bg-red-950/80 border border-red-800 text-red-400 flex items-center justify-center font-black text-sm mb-3">
                  🤝
                </div>
                <h3 className="text-sm font-bold text-white mb-2">บริการหลังการขาย Toyota Service</h3>
                <ul className="space-y-2 text-xs text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 font-bold">✓</span>
                    <span>ทีมวิศวกรและช่างเทคนิคผู้เชี่ยวชาญดูแลถึงหน้างานทั่วประเทศไทย</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 font-bold">✓</span>
                    <span>รับประกันอะไหล่แท้ <strong>Toyota Genuine Parts 100%</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 font-bold">✓</span>
                    <span>สัญญาบำรุงรักษาเชิงป้องกัน (PM) ตรวจเช็กสม่ำเสมอ</span>
                  </li>
                </ul>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-800/80 text-[11px] text-red-400 font-semibold">
                มั่นใจด้วยมาตรฐานระดับโลกของโตโยต้า
              </div>
            </div>
          </div>

          {/* Contact & Next Steps Banner */}
          <div className="bg-gradient-to-r from-red-950/80 to-slate-950 border border-red-800/50 rounded-xl p-3 flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-red-400 shrink-0" />
              <span>พร้อมส่งมอบใบเสนอราคาอย่างเป็นทางการและนัดหมายสาธิตการทำงานที่หน้างานจริง</span>
            </div>
            <button 
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white font-bold px-3 py-1.5 rounded-lg text-xs transition-colors cursor-pointer shrink-0"
            >
              <Printer size={13} /> พิมพ์สไลด์ / PDF
            </button>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-slate-500 border-t border-slate-800 pt-3">
            <span>Toyota Material Handling Thailand — Final Executive Summary</span>
            <span>สไลด์ 06 / 06</span>
          </div>
        </div>
      )
    }
  ];

  /* ─────────────────────────────────────────────────────────────────────────────
     RENDER COMPONENT
  ───────────────────────────────────────────────────────────────────────────── */
  return (
    <div className="proposal-view-container min-h-screen bg-slate-950 text-slate-100 flex flex-col select-none">
      {/* ─── Top Presentation Navigation Toolbar ────────────────────────── */}
      <div className="no-print bg-slate-900 border-b border-slate-800 px-4 py-2.5 flex items-center justify-between shrink-0 z-20 gap-4 flex-wrap">
        {/* Left: Back + Mode Switcher */}
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white rounded-lg text-xs font-semibold transition-colors cursor-pointer border border-slate-700"
          >
            <ArrowLeft size={15} />
            กลับสู่การจำลอง
          </button>

          {/* View Mode Switcher */}
          <div className="flex items-center bg-slate-950 p-0.5 rounded-lg border border-slate-800">
            <button
              onClick={() => setViewMode('slides')}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-bold transition-all cursor-pointer ${
                viewMode === 'slides' 
                  ? 'bg-red-600 text-white shadow-xs' 
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Tv size={14} /> สไลด์ PowerPoint
            </button>
            <button
              onClick={() => setViewMode('document')}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-bold transition-all cursor-pointer ${
                viewMode === 'document' 
                  ? 'bg-red-600 text-white shadow-xs' 
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <FileText size={14} /> เอกสารรายงาน A4
            </button>
          </div>
        </div>

        {/* Center: Slide Stepper Controls (only in slides mode) */}
        {viewMode === 'slides' && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveSlide(prev => Math.max(prev - 1, 0))}
              disabled={activeSlide === 0}
              className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border border-slate-700 cursor-pointer"
              title="สไลด์ก่อนหน้า (Arrow Left)"
            >
              <ChevronLeft size={16} />
            </button>

            <span className="text-xs font-bold text-slate-300 min-w-[130px] text-center">
              สไลด์ {activeSlide + 1} / {SLIDES.length}: <span className="text-red-400">{SLIDES[activeSlide].title}</span>
            </span>

            <button
              onClick={() => setActiveSlide(prev => Math.min(prev + 1, SLIDES.length - 1))}
              disabled={activeSlide === SLIDES.length - 1}
              className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border border-slate-700 cursor-pointer"
              title="สไลด์ถัดไป (Arrow Right / Space)"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsEditingOnsite(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-amber-300 hover:text-amber-200 border border-slate-700 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
          >
            <Edit3 size={13} /> ข้อมูลหน้างาน
          </button>

          {viewMode === 'slides' && (
            <button
              onClick={toggleFullscreen}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
              title="นำเสนอแบบเต็มจอ (F5)"
            >
              {isFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
              {isFullscreen ? 'ออกเต็มจอ' : 'เต็มจอ'}
            </button>
          )}

          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition-colors shadow-xs cursor-pointer"
          >
            <Printer size={14} /> พิมพ์ / PDF
          </button>
        </div>
      </div>

      {/* ─── Main Content Area ──────────────────────────────────────────── */}
      {viewMode === 'slides' ? (
        <div className="flex-1 flex flex-col justify-between p-3 sm:p-6 lg:p-8 max-w-6xl w-full mx-auto">
          {/* Slide Deck Canvas (16:9 Aspect Ratio) */}
          <div 
            ref={slideDeckRef}
            className="w-full aspect-[16/9] min-h-[460px] sm:min-h-[520px] max-h-[720px] bg-slate-900 rounded-2xl border border-slate-800 shadow-2xl overflow-hidden relative flex flex-col"
          >
            {SLIDES[activeSlide].render()}
          </div>

          {/* Bottom Thumbnail Strip for Slides */}
          <div className="mt-4 grid grid-cols-6 gap-2 no-print">
            {SLIDES.map((s, idx) => (
              <button
                key={s.id}
                onClick={() => setActiveSlide(idx)}
                className={`p-2 rounded-xl text-left border transition-all cursor-pointer ${
                  activeSlide === idx 
                    ? 'bg-red-950/40 border-red-500 ring-2 ring-red-500/30' 
                    : 'bg-slate-900/80 border-slate-800 hover:border-slate-700 hover:bg-slate-800/60'
                }`}
              >
                <div className="flex items-center justify-between text-[10px] mb-1">
                  <span className={`font-black ${activeSlide === idx ? 'text-red-400' : 'text-slate-500'}`}>
                    0{idx + 1}
                  </span>
                  {activeSlide === idx && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
                </div>
                <div className="text-xs font-bold text-slate-200 truncate">{s.title}</div>
                <div className="text-[10px] text-slate-400 truncate mt-0.5">{s.subtitle}</div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        /* ─── Formal Printable A4 Document View ───────────────────────── */
        <div className="flex-1 overflow-auto bg-gray-100 py-8 px-4 sm:px-6">
          <div 
            ref={printRef}
            className="printable-proposal max-w-4xl mx-auto bg-white text-gray-900 rounded-2xl shadow-xl border border-gray-200 overflow-hidden print:shadow-none print:border-0 print:rounded-none print:max-w-full"
          >
            {/* Document Cover Header */}
            <div className="bg-gradient-to-r from-gray-950 via-gray-900 to-red-950 text-white p-8 sm:p-10 border-b-4 border-red-600 relative overflow-hidden">
              <div className="relative z-10 flex flex-col md:flex-row md:items-start justify-between gap-6">
                <div>
                  <span className="bg-red-600 text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded">
                    Toyota Solution Proposal
                  </span>
                  <h1 className="text-2xl sm:text-3xl font-extrabold text-white mt-3">
                    {onsiteForm.title}
                  </h1>
                  <p className="text-gray-300 text-sm mt-1 flex items-center gap-2">
                    <Building2 size={16} className="text-red-400" />
                    Customer: <strong className="text-white">{onsiteForm.customer_name}</strong>
                  </p>
                </div>
                <div className="text-right flex flex-col items-end">
                  <div className="mb-2 bg-white rounded-lg px-3 py-1.5">
                    <CompanyLogo variant="proposal" showSubtitle={false} />
                  </div>
                  <div className="text-xs text-gray-400">{companyName || 'Toyota Material Handling Thailand'}</div>
                  <div className="text-xs text-gray-400 mt-1">{new Date().toLocaleDateString('th-TH')}</div>
                </div>
              </div>
            </div>

            {/* Document Body */}
            <div className="p-8 sm:p-10 space-y-8">
              {/* Section 1: Customer & Site Parameters */}
              <section>
                <h2 className="text-base font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
                  <Building2 size={18} className="text-red-600" />
                  1. ข้อมูลลูกค้าหน้างานและความท้าทายปัจจุบัน (Customer Onsite Profile & Challenges)
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-xs">
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <span className="text-gray-500">พื้นที่คลังสินค้า:</span>
                    <div className="font-bold text-gray-900 text-sm mt-0.5">{Number(onsiteForm.warehouse_area_sqm).toLocaleString()} ตร.ม.</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <span className="text-gray-500">ความสูงใต้คาน:</span>
                    <div className="font-bold text-gray-900 text-sm mt-0.5">{onsiteForm.clear_height_m} เมตร</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <span className="text-gray-500">สภาพแวดล้อม:</span>
                    <div className="font-bold text-gray-900 text-sm mt-0.5">{onsiteForm.temperature}</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <span className="text-gray-500">กะการทำงาน:</span>
                    <div className="font-bold text-gray-900 text-sm mt-0.5">{onsiteForm.working_shifts}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-xs">
                  <div className="bg-red-50/70 border border-red-200 p-3.5 rounded-xl">
                    <span className="font-bold text-red-800 block mb-1">ปัญหาหน้างานปัจจุบัน:</span>
                    <ul className="space-y-1 text-gray-700 list-disc list-inside">
                      {onsiteForm.pain_points.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                  </div>
                  <div className="bg-emerald-50/70 border border-emerald-200 p-3.5 rounded-xl">
                    <span className="font-bold text-emerald-800 block mb-1">เป้าหมายโครงการ:</span>
                    <ul className="space-y-1 text-gray-700 list-disc list-inside">
                      {onsiteForm.goals.map((g, i) => <li key={i}>{g}</li>)}
                    </ul>
                  </div>
                </div>
              </section>

              {/* Section 2: Storage & Layout */}
              <section>
                <h2 className="text-base font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
                  <Layers size={18} className="text-red-600" />
                  2. ผังคลังสินค้าและระบบจัดเก็บที่ออกแบบ (Warehouse Layout & Capacity)
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-center">
                  <div className="bg-red-50 border border-red-100 p-3 rounded-xl">
                    <div className="text-xs text-red-600 font-semibold">ความจุจัดเก็บรวม</div>
                    <div className="text-2xl font-black text-gray-900">{totalPallets.toLocaleString()}</div>
                    <div className="text-[11px] text-gray-500">พาเลท (Pallets)</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-100 p-3 rounded-xl">
                    <div className="text-xs text-blue-600 font-semibold">Throughput เฉลี่ย</div>
                    <div className="text-2xl font-black text-gray-900">{throughput}</div>
                    <div className="text-[11px] text-gray-500">พาเลท / ชม.</div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-100 p-3 rounded-xl">
                    <div className="text-xs text-emerald-600 font-semibold">รถยกที่แนะนำ</div>
                    <div className="text-2xl font-black text-gray-900">{recommendedFleet}</div>
                    <div className="text-[11px] text-gray-500">คัน (Fleet Size)</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-100 p-3 rounded-xl">
                    <div className="text-xs text-amber-600 font-semibold">ระยะคืนทุน</div>
                    <div className="text-2xl font-black text-gray-900">{onsiteForm.payback_months}</div>
                    <div className="text-[11px] text-gray-500">เดือน (Payback)</div>
                  </div>
                </div>
              </section>

              {/* Section 3: Equipment Specs */}
              <section>
                <h2 className="text-base font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
                  <Award size={18} className="text-red-600" />
                  3. รถยกโตโยต้าและสเปกทางวิศวกรรม (Recommended Toyota Forklift)
                </h2>
                <div className="mt-3 bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row gap-5 items-center">
                  <div className="w-36 h-28 bg-white rounded-lg border border-gray-200 p-2 flex items-center justify-center shrink-0">
                    {forklift?.image_url ? (
                      <img src={forklift.image_url} alt="" className="max-h-full max-w-full object-contain mix-blend-multiply" />
                    ) : (
                      <Truck size={36} className="text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 text-xs">
                    <div className="text-red-600 font-bold uppercase">{forklift?.series}</div>
                    <div className="text-lg font-black text-gray-900">{forklift?.model}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                      <div className="bg-white p-2 rounded border border-gray-200">
                        <span className="text-gray-500">พิกัดยก:</span>
                        <div className="font-bold text-gray-800">{forklift?.load_capacity_kg} kg</div>
                      </div>
                      <div className="bg-white p-2 rounded border border-gray-200">
                        <span className="text-gray-500">ระยะยกสูง:</span>
                        <div className="font-bold text-gray-800">{((forklift?.lift_height_mm || 7000) / 1000).toFixed(2)} m</div>
                      </div>
                      <div className="bg-white p-2 rounded border border-gray-200">
                        <span className="text-gray-500">ช่องทางวิ่ง:</span>
                        <div className="font-bold text-gray-800">{forklift?.aisle_width_1000x1200_mm || 2750} mm</div>
                      </div>
                      <div className="bg-white p-2 rounded border border-gray-200">
                        <span className="text-gray-500">ระบบไฟฟ้า:</span>
                        <div className="font-bold text-gray-800">{forklift?.battery_voltage_v || 48}V Li-ion</div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 4: Sign-off */}
              <div className="pt-6 border-t border-gray-200 flex justify-between items-center text-xs text-gray-500">
                <span>Toyota Material Handling (Thailand) Co., Ltd.</span>
                <span>สงวนลิขสิทธิ์ © 2026</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Edit Onsite Information Modal ─────────────────────────────── */}
      {isEditingOnsite && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full p-6 text-white shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Edit3 size={18} className="text-amber-400" />
                แก้ไขข้อมูลลูกค้าหน้างาน (Onsite Customer Profile)
              </h3>
              <button
                onClick={() => setIsEditingOnsite(false)}
                className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 font-semibold mb-1">ชื่อลูกค้า (Customer Name)</label>
                  <input
                    type="text"
                    value={onsiteForm.customer_name}
                    onChange={(e) => setOnsiteForm(prev => ({ ...prev, customer_name: e.target.value }))}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-red-500 text-xs"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 font-semibold mb-1">ชื่อโครงการ (Proposal Title)</label>
                  <input
                    type="text"
                    value={onsiteForm.title}
                    onChange={(e) => setOnsiteForm(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-red-500 text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div>
                  <label className="block text-slate-400 font-semibold mb-1">พื้นที่คลัง (ตร.ม.)</label>
                  <input
                    type="number"
                    value={onsiteForm.warehouse_area_sqm}
                    onChange={(e) => setOnsiteForm(prev => ({ ...prev, warehouse_area_sqm: e.target.value }))}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-red-500 text-xs"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 font-semibold mb-1">ความสูงใต้คาน (ม.)</label>
                  <input
                    type="number"
                    step="0.5"
                    value={onsiteForm.clear_height_m}
                    onChange={(e) => setOnsiteForm(prev => ({ ...prev, clear_height_m: e.target.value }))}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-red-500 text-xs"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 font-semibold mb-1">กะการทำงาน & เวลา</label>
                  <input
                    type="text"
                    value={onsiteForm.working_shifts}
                    onChange={(e) => setOnsiteForm(prev => ({ ...prev, working_shifts: e.target.value }))}
                    placeholder="เช่น 2 กะ (16 ชม./วัน)"
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-red-500 text-xs"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 font-semibold mb-1">สภาพอุณหภูมิ</label>
                  <input
                    type="text"
                    value={onsiteForm.temperature}
                    onChange={(e) => setOnsiteForm(prev => ({ ...prev, temperature: e.target.value }))}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-red-500 text-xs"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 font-semibold mb-1">คืนทุน (เดือน)</label>
                  <input
                    type="text"
                    value={onsiteForm.payback_months}
                    onChange={(e) => setOnsiteForm(prev => ({ ...prev, payback_months: e.target.value }))}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-red-500 text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 font-semibold mb-1">ปัญหาและความท้าทายหน้างานเดิม (Pain Points - บรรทัดละ 1 ข้อ)</label>
                <textarea
                  rows={3}
                  value={onsiteForm.pain_points.join('\n')}
                  onChange={(e) => setOnsiteForm(prev => ({ ...prev, pain_points: e.target.value.split('\n').filter(Boolean) }))}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-white focus:outline-none focus:border-red-500 text-xs font-mono"
                />
              </div>

              <div>
                <label className="block text-slate-400 font-semibold mb-1">เป้าหมายที่ต้องการ (Project Target Goals - บรรทัดละ 1 ข้อ)</label>
                <textarea
                  rows={3}
                  value={onsiteForm.goals.join('\n')}
                  onChange={(e) => setOnsiteForm(prev => ({ ...prev, goals: e.target.value.split('\n').filter(Boolean) }))}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-white focus:outline-none focus:border-red-500 text-xs font-mono"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-slate-800 pt-4 mt-5">
              <button
                onClick={() => setIsEditingOnsite(false)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleSaveOnsiteDetails}
                disabled={isSaving}
                className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition-colors shadow-xs cursor-pointer flex items-center gap-1.5"
              >
                {isSaving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
